class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // The input field where the user types their query
    this.input = this.querySelector('input[type="search"]');
    // The container where predictive search results will be injected
    this.predictiveSearchResults = this.querySelector('#ttg-predictive-search');
    // A simple cache object to store previously fetched results, preventing redundant network calls
    this.cache = {};

    // Add a debounced input event listener to avoid flooding the API on every keystroke
    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
  }

  /**
   * Triggered every time the user types in the search input (after debounce).
   * Checks if input is empty, if not, fetches search results.
   */
  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      // If the search input is cleared, hide predictive results
      this.close();
      return;
    }

    // Fetch and render results for the current search term
    this.getSearchResults(searchTerm);
  }

  /**
   * Fetches predictive search results from Shopify's Predictive Search API.
   * Uses a cache to avoid repeated network calls for the same query.
   * @param {string} searchTerm The user-entered search term.
   */
  getSearchResults(searchTerm) {
    // Check the cache first
    if (this.cache[searchTerm]) {
      console.debug(`Using cached results for: "${searchTerm}"`);
      this.renderResults(this.cache[searchTerm]);
      return;
    }

    console.debug(`Fetching predictive search suggestions for: "${searchTerm}"`);

    fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=ttg-predictive-search&resources[type]=product,query&resources[options][fields]=title,variants.title,variants.sku,variants.barcode,product_type`)
      .then((response) => {
        console.log('Response received:', response.status);
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          console.error('Predictive search fetch error:', error);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        console.log('Response text received:', text.substring(0,100)); // Log first 200 chars to confirm we got something
        this.renderResults(text);
      })
      .catch((error) => {
        console.error('Predictive search error in catch block:', error);
        this.close();
      });
  }

    /**
     * Parses the fetched HTML response, extracts the predictive search results,
     * and injects them into the DOM. Also provides basic fallback and logging.
     * @param {string} text The raw HTML response from the Predictive Search API.
     */
      renderResults(text) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    const section = doc.querySelector('#shopify-section-ttg-predictive-search');
   
    if (!section) {
      console.warn('No predictive search section found in response.');
      this.close();
      return;
    }
  
    this.predictiveSearchResults.innerHTML = section.innerHTML;
  
    const queriesList = this.predictiveSearchResults.querySelector('ul[aria-labelledby="predictive-search-queries"]');
    const productsList = this.predictiveSearchResults.querySelector('ul[aria-labelledby="predictive-search-products"]');
  
    if (queriesList) {
      const queryItems = queriesList.querySelectorAll('li');
      queryItems.forEach((item) => {
        const link = item.querySelector('a');
        if (!link || !link.textContent.trim().length) {
          item.remove();
        }
      });
  
      if (queriesList.children.length === 0) {
        const queriesHeading = this.predictiveSearchResults.querySelector('#predictive-search-queries');
        if (queriesHeading) {
          queriesHeading.remove();
        }
        queriesList.remove();
        console.debug('All queries were empty and have been removed.');
      }
    }
  
    console.debug('queriesList:', queriesList, queriesList ? queriesList.children.length : 'no queriesList');
    console.debug('productsList:', productsList, productsList ? productsList.children.length : 'no productsList');
  
    const noQueries = !queriesList || queriesList.children.length === 0;
    const noProducts = !productsList || productsList.children.length === 0;
  
    // Helper function to escape HTML
    function escapeHTML(input) {
      return input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }
  
    // Function to generate no-results HTML
    function getNoResultsHTML(safeSearchTerm) {
      return `
        <div class="no-suggestions">
          <p><strong>We couldn’t find any results for “${safeSearchTerm}”.</strong></p>
          <p>Try the following:</p>
          <ul>
            <li><strong>Check your spelling</strong> and try again.</li>
            <li>Use <strong>fewer</strong> or more <strong>general search terms</strong>.</li>
            <li><a href="/collections/all">Browse our categories</a> to see what we offer.</li>
            <li><a href="/pages/contact-us">Contact our team</a> or use our live chat for assistance.</li>
          </ul>
        </div>
      `;
    }
  
    // Show fallback message if no results are found
    if (noQueries && noProducts) {
      const safeSearchTerm = escapeHTML(this.input.value.trim());
      this.predictiveSearchResults.innerHTML = getNoResultsHTML(safeSearchTerm);
      console.info('No suggestions found for:', safeSearchTerm);
      this.open(); // Ensure the dropdown still opens for no-results case
      return; // Stop further execution since no results are available
    }
  
    console.info('Rendered results successfully.');
    this.open();
  }

  /**
   * Opens the predictive search results container by making it visible.
   * Also sets aria-expanded to true for accessibility.
   */
  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  }

  /**
   * Closes the predictive search results, hiding them from view.
   * Also sets aria-expanded to false for accessibility.
   */
  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
  }

  /**
   * Returns a debounced version of the given function. The function will only
   * run after the specified wait time has elapsed without a new call.
   * This helps reduce unnecessary fetch requests as the user types.
   * @param {Function} fn The function to debounce.
   * @param {number} wait The debounce delay in milliseconds.
   * @returns {Function} The debounced function.
   */
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

// Define the custom element so it can be used as <ttg-predictive-search></ttg-predictive-search>
customElements.define('ttg-predictive-search', PredictiveSearch);